import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Section, Box, Flex, Text, Button, Table, Heading, Separator, IconButton } from '@radix-ui/themes';
import { FileIcon } from '@radix-ui/react-icons';

import PageTitle from 'components/layout/PageTitle';
import Editor from 'components/editor';
import 'components/board/boardDetail.scss';

import { instance } from 'api/axios.instance';
import { selectType, selectRegion } from 'store/slices/users';
import { localList, regions } from 'app/constants';
import { formatIssueYear } from './localIssueYear';

// 지역 이슈 파인더 > 상세
export default function LocalIssueFinderDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const type = useSelector(selectType);
  const userRegion = useSelector(selectRegion);
  const fileInputRef = useRef(null);

  const [issue, setIssue] = useState(null);
  const [files, setFiles] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await instance(`/local-issue/${id}`, { method: 'GET' });
      if (res.data.status === 200) setIssue(res.data.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        alert('접근 권한이 없는 지역의 이슈입니다.');
        navigate('/localIssueFinder', { replace: true });
        return;
      }
      throw error;
    }
  }, [id, navigate]);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await instance(`/local-issue/${id}/files`, { method: 'GET' });
      setFiles(res.data.data || []);
    } catch {
      setFiles([]);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
    fetchFiles();
  }, [fetchDetail, fetchFiles]);

  // linkedFormId + fieldValuesJson 있으면 템플릿 기반 HWPX, 없으면 이슈 내용 기반 HWPX
  const handleDownload = async () => {
    setDownloading(true);
    try {
      let response;
      if (issue?.linkedFormId && issue?.fieldValuesJson) {
        const values = JSON.parse(issue.fieldValuesJson);
        response = await instance(`/form/submit/${issue.linkedFormId}`, {
          method: 'POST',
          data: { values },
          responseType: 'blob',
          timeout: 30000
        });
      } else {
        response = await instance(`/local-issue/${id}/download`, {
          method: 'GET',
          responseType: 'blob',
          timeout: 30000
        });
      }
      const filename = `${issue?.title || '지역이슈'}.hwpx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = '다운로드 중 오류가 발생했습니다.';
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) msg += `\n(${parsed.message})`;
        }
      } catch {}
      alert(msg);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await instance(`/local-issue/${id}/files`, {
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchFiles();
    } catch {
      alert('파일 업로드에 실패했습니다.');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('이슈를 삭제하시겠습니까?')) return;
    try {
      await instance(`/local-issue/${id}`, { method: 'DELETE' });
      navigate('/localIssueFinder');
    } catch {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleFileDelete = async fileId => {
    if (!window.confirm('파일을 삭제하시겠습니까?')) return;
    try {
      await instance(`/local-issue/files/${fileId}`, { method: 'DELETE' });
      fetchFiles();
    } catch {
      alert('파일 삭제에 실패했습니다.');
    }
  };

  const handleFileDownload = async (fileUrl, fileName) => {
    try {
      const res = await instance(fileUrl, { method: 'GET', responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  const formatFileSize = bytes => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const ownRegion = regions[userRegion] ?? userRegion ?? '';
  const canEdit = type === 'master' || type === 'admin' || (type === 'user' && !!issue?.region && ownRegion === issue.region);
  const canDelete = type === 'master' || type === 'admin';

  const regionName = localList.find(r => r.code === issue?.region)?.name || issue?.region || '';

  if (!issue) return null;

  return (
    <Section className="template-form-wrap">
      <PageTitle title="지역 이슈 상세" />

      {/* 제목 + 메타 정보 */}
      <Box className="box box-white">
        <Box className="post-title">
          <Heading as="h4" className="title3 issue-title">
            {issue.title}
          </Heading>
        </Box>
        <Flex align="center" wrap="wrap" className="post-info" gap="var(--space-4)">
          <Text as="span" className="body2">
            지역 : {regionName}
          </Text>
          <Text as="span" className="body2">
            분류 연도 : {formatIssueYear(issue.year)}
          </Text>
          <Flex align="center" gap="5" ml="auto">
            <Text as="p" className="body2">
              작성자 : {issue.author || '-'}
            </Text>
            <Separator orientation="vertical" />
            <Text as="p" className="body2">
              등록일 : {issue.createDate?.slice(0, 10) || '-'}
            </Text>
            {issue.updateDate && (
              <>
                <Separator orientation="vertical" />
                <Text as="p" className="body2">
                  최종 수정일 : {issue.updateDate.slice(0, 10)}
                </Text>
              </>
            )}
          </Flex>
        </Flex>
      </Box>

      {/* 본문 */}
      <Box className="box box-white">
        <Box className="post-box">
          <Editor editorData={issue.content || ''} setEditorData={() => {}} board="localIssueFinder" readOnly={true} />
        </Box>
      </Box>

      {/* 첨부파일 */}
      <Box className="box box-white">
        <Flex align="center" gap="var(--space-1)" mb="var(--space-3)">
          <IconButton size="1" className="icon-btn">
            <FileIcon width={24} height={24} />
          </IconButton>
          <Text as="p" className="sub-title1">
            첨부파일
          </Text>
        </Flex>
        {canEdit && (
          <Flex justify="end" mb="var(--space-2)">
            <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
            <Button size="2" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
              {uploadingFile ? '업로드 중...' : '파일 첨부'}
            </Button>
          </Flex>
        )}
        {files.length === 0 ? (
          <Text size="2" color="gray">
            첨부파일이 없습니다.
          </Text>
        ) : (
          <Table.Root variant="surface" size="2">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>파일명</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="90px" justify="center">
                  크기
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="130px" justify="center">
                  등록일
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="180px" justify="center">
                  관리
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {files.map(f => (
                <Table.Row key={f.id} align="center">
                  <Table.Cell>
                    <Text
                      size="2"
                      style={{
                        cursor: 'pointer',
                        color: 'var(--accent-9)',
                        textDecoration: 'underline',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '420px',
                        display: 'block'
                      }}
                      onClick={() => handleFileDownload(f.fileUrl, f.fileName)}
                    >
                      {f.fileName}
                    </Text>
                  </Table.Cell>
                  <Table.Cell justify="center">
                    <Text size="2">{formatFileSize(f.fileSize)}</Text>
                  </Table.Cell>
                  <Table.Cell justify="center">
                    <Text size="2">{f.regDt}</Text>
                  </Table.Cell>
                  <Table.Cell justify="center">
                    <Flex gap="2" justify="center">
                      <Button size="1" variant="outline" onClick={() => handleFileDownload(f.fileUrl, f.fileName)}>
                        다운로드
                      </Button>
                      {canEdit && (
                        <Button size="1" variant="outline" color="red" onClick={() => handleFileDelete(f.id)}>
                          삭제
                        </Button>
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>

      {/* 하단 버튼 */}
      <Flex justify="between">
        <Button variant="outline" onClick={() => navigate('/localIssueFinder')}>
          목록
        </Button>
        <Flex gap="2">
          <Button size="2" variant="outline" onClick={handleDownload} disabled={downloading}>
            {downloading ? '생성 중...' : '한글 다운로드'}
          </Button>
          {canEdit && (
            <Button size="2" onClick={() => navigate(`/localIssueFinder/write/${id}`)}>
              수정
            </Button>
          )}
          {canDelete && (
            <Button size="2" variant="outline" color="red" onClick={handleDelete}>
              삭제
            </Button>
          )}
        </Flex>
      </Flex>
    </Section>
  );
}
